// ── Capstone section (Wave 9 → F15 READ wired; participant OWN-ONLY) ─────────
// The capstone READ is NOW LIVE. This section reads the participant's OWN
// submission(s) via GET /cohorts/:cohort_id/capstone-submissions — the server
// scope-isolates the read to the participant's OWN submission (the participant
// ownership predicate runs inside the SELECT). We read by the participant's OWN
// cohort id only; we NEVER request a cross-participant or cohort-roster view.
//
// ISOLATION (FR-PDASH-001): own data only — the section reads the participant's
// own cohort (scope=mine) then its capstone submissions (server-isolated to own).
// REPORT-EXCLUSION (FR-REPORT-001): a participant must not learn a report even
// EXISTS — so the capstone `reportId` reference is NOT surfaced here at all (no
// "report" column, no report link, no report text). The participant sees only
// their submission STATUS; the report linkage stays an admin/staff concern.
//
// BACKEND GAP (still): there is NO participant-scoped capstone SUBMIT route, so
// the upload control stays an honest disabled placeholder — it POSTS NOTHING
// (never to the staff-only intake). The structured capstone JSON schema is an OPEN
// DEPENDENCY (pending_schema, MATURITY-GATED) so no evaluation result is shown.

import { Alert, Button, CollectionPagination, CollectionToolbar, DataTableV2, Module, ModuleBody, ModuleHead } from "@vjal/ui";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import type { CoachingCohortDto, ListResponse } from "../../services/coaching-dtos";
import type { CoachingCapstoneSubmissionsResponseDto } from "../../services/coaching-readiness-dtos";
import { CAPSTONE_SCHEMA_STATE } from "../capstone/capstone-helpers";
import {
  participantQueryKeys,
  readOwnCapstoneSubmissions,
  readOwnCohorts,
} from "./participant-dashboard-helpers";
import { Upload } from "lucide-react";

import {
  useParticipantCapstoneCollection,
  type ParticipantCapstoneRow,
} from "./participant-capstone-collection";


export function ParticipantCapstoneSection() {
  const cohortsQuery = useHonestQuery<ListResponse<CoachingCohortDto>>(
    participantQueryKeys.ownCohorts(),
    readOwnCohorts
  );
  const ownCohortId = cohortsQuery.data?.items?.[0]?.id ?? "";

  const capstoneQuery = useHonestQuery<CoachingCapstoneSubmissionsResponseDto>(
    participantQueryKeys.capstone(ownCohortId || "missing"),
    () => readOwnCapstoneSubmissions(ownCohortId),
    {
      enabled: Boolean(ownCohortId),
      disabledPosture: "empty",
      isEmpty: (data) => (data.capstoneSubmissions ?? []).length === 0,
    }
  );

  const bodyPosture = cohortsQuery.posture ?? (!ownCohortId ? "empty" : capstoneQuery.posture);
  const rows = (capstoneQuery.data?.capstoneSubmissions ?? []) as ParticipantCapstoneRow[];
  const collection = useParticipantCapstoneCollection(rows);

  return (
    <Module span={12}>
      <ModuleHead title="Capstone" subtitle="Your capstone submission and its evaluation status (your data only)." />
      <ModuleBody className="space-y-4" data-testid="participant-capstone-section">
        {bodyPosture !== null ? (
          <div data-honest-posture={bodyPosture}>
            <HonestState
              posture={bodyPosture}
              gate={cohortsQuery.gate ?? capstoneQuery.gate}
              reference={cohortsQuery.reference ?? capstoneQuery.reference}
              onRetry={cohortsQuery.posture !== null ? cohortsQuery.retry : capstoneQuery.retry}
              loadingVariant="rows"
              title={bodyPosture === "empty" ? "No capstone submission yet" : undefined}
              message={
                bodyPosture === "empty"
                  ? "You don't have a capstone submission yet. Once a staff intake records yours, its status appears here."
                  : undefined
              }
            />
          </div>
        ) : (
          <>
            <CollectionToolbar collection={collection} className="rounded-xl border border-line" stableChipNames />
            <div className="rounded-xl border border-line bg-white">
              <DataTableV2 collection={collection} caption="Your capstone submission" />
              {collection.uiState === "ready" && <CollectionPagination collection={collection} className="px-4" />}
            </div>
          </>
        )}

        <Alert variant="info">
          The structured evaluation result format is still being finalized (status: {CAPSTONE_SCHEMA_STATE.status}), so no
          evaluation result is shown yet. Participant self-upload isn't available yet — your capstone is recorded by staff.
        </Alert>

        {/* Disabled by design: there is NO participant submit endpoint, so this
            fires NOTHING and never implies a capstone was submitted. */}
        <Button type="button" variant="secondary" leftIcon={<Upload size={16} />} disabled aria-label="Upload capstone">
          Upload capstone (not available yet)
        </Button>
      </ModuleBody>
    </Module>
  );
}
